import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Alert } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';
import EmployeeInduction from '../components/EmployeeInduction';
import LicenceUpload from '../components/LicenceUpload';

// CS Staff → View Staff: the staff table, with each person's induction status
export default function EmployeeList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner  = user?.role === 'owner';

  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [notice,    setNotice]    = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get('/employees');
      setEmployees(data.employees || []);
    } catch (err) {
      setNotice({ type:'error', text: err.response?.data?.message || 'Failed to load staff' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const [licenceFor, setLicenceFor] = useState(null);   // the staff member whose driver licence is open

  const handleDelete = async (e) => {
    if (!await confirmDelete(`"${String(e.fullName).replace(/[&<>"']/g, ch => '&#' + ch.charCodeAt(0) + ';')}" and all of their details (including bank and medical information) and their induction record will be permanently removed.`)) return;
    setDeletingId(e._id);
    try {
      await api.delete(`/employees/${e._id}`);
      setEmployees(prev => prev.filter(x => x._id !== e._id));
    } catch (err) {
      setNotice({ type:'error', text: err.response?.data?.message || 'Failed to delete' });
    } finally { setDeletingId(null); }
  };

  const completed = employees.filter(e => e.induction?.status === 'completed').length;

  const th = { padding:'10px 12px', textAlign:'left', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' };
  const td = { padding:'11px 12px', fontSize:'13px', color:'var(--text)', verticalAlign:'middle' };

  return (
    <div style={T.page}>
      <PageHeader title="CS Staff" actions={
        <button onClick={()=>navigate('/staff/new')} style={T.btnPrimary}>
          <i className="ti ti-plus" aria-hidden="true"/> Add Staff
        </button>
      }/>

      <div style={{ maxWidth:'1000px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {notice && <Alert type={notice.type}>{notice.text}</Alert>}

        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:'10px', marginBottom:'16px' }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px' }}>
            <div style={{ fontSize:'11px', color:'var(--muted)', marginBottom:'6px' }}>Staff</div>
            <div style={{ fontSize:'22px', fontWeight:'500', color:'var(--blue)' }}>{employees.length}</div>
          </div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px' }}>
            <div style={{ fontSize:'11px', color:'var(--muted)', marginBottom:'6px' }}>Inductions completed</div>
            <div style={{ fontSize:'22px', fontWeight:'500', color:'var(--green)' }}>{completed}<span style={{ fontSize:'13px', color:'var(--muted)', fontWeight:'400' }}> / {employees.length}</span></div>
          </div>
        </div>

        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:'var(--muted)' }}>
              <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
            </div>
          ) : employees.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center' }}>
              <i className="ti ti-users-group" style={{ fontSize:'32px', color:'var(--hint)', display:'block', marginBottom:'12px' }} aria-hidden="true"/>
              <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'6px' }}>No staff added yet</div>
              <div style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'16px' }}>Add a new staff member with their employee details, then send their induction.</div>
              <button onClick={()=>navigate('/staff/new')} style={T.btnPrimary}>Add Staff</button>
            </div>
          ) : (
            <div className="table-scroll">
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>{['Staff ID','Name','Mobile','Start Date','Induction','Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {employees.map(e => (
                    <tr key={e._id} onClick={()=>navigate(`/staff/${e._id}`)} style={{ cursor:'pointer', transition:'background .1s' }}
                      onMouseEnter={ev=>ev.currentTarget.style.background='var(--bg)'}
                      onMouseLeave={ev=>ev.currentTarget.style.background='transparent'}>
                      <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', fontWeight:'600', color:'var(--blue)' }}>{e.staffId || '—'}</span></td>
                      <td style={td}>
                        <div style={{ fontWeight:'500' }}>{e.fullName}</div>
                        <div style={{ fontSize:'11px', color:'var(--muted)' }}>{e.email}</div>
                      </td>
                      <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px' }}>{e.mobilePhone}</span></td>
                      <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px' }}>{e.startDate ? new Date(e.startDate).toLocaleDateString('en-AU') : '—'}</span></td>
                      <td style={td} onClick={ev=>ev.stopPropagation()}>
                        <EmployeeInduction employee={e} onChanged={fetchAll} onNotice={setNotice}/>
                      </td>
                      <td style={td} onClick={ev=>ev.stopPropagation()}>
                        <div style={{ display:'flex', gap:'4px' }}>
                          {(e.licenceFront?.uploaded || e.licenceBack?.uploaded) && (
                            <button onClick={()=>setLicenceFor(e)} title="View driver licence" style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)' }}>
                              <i className="ti ti-id" style={{ fontSize:'13px' }} aria-label="View driver licence"/>
                            </button>
                          )}
                          <button onClick={()=>navigate(`/staff/${e._id}/edit`)} title="Edit" style={{ ...T.btnIcon, color:'var(--amber)', borderColor:'rgba(133,79,11,0.25)' }}>
                            <i className="ti ti-edit" style={{ fontSize:'13px' }} aria-label="Edit"/>
                          </button>
                          {isOwner && (
                            <button onClick={()=>handleDelete(e)} disabled={deletingId===e._id} title="Delete" style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                              <i className="ti ti-trash" style={{ fontSize:'13px' }} aria-label="Delete"/>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {licenceFor && (
        <div onClick={()=>setLicenceFor(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
          <div onClick={ev=>ev.stopPropagation()} style={{ background:'var(--surface)', borderRadius:'var(--radius-lg)', padding:'20px', width:'100%', maxWidth:'420px', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
              <div style={{ fontSize:'15px', fontWeight:'500', color:'var(--text)' }}>Driver licence — {licenceFor.fullName}</div>
              <button onClick={()=>setLicenceFor(null)} title="Close" style={{ ...T.btnIcon }}><i className="ti ti-x" style={{ fontSize:'14px' }} aria-label="Close"/></button>
            </div>
            {['front', 'back'].map(side => (
              <LicenceUpload key={side} label={side === 'front' ? 'Front' : 'Back'} meta={licenceFor[side === 'front' ? 'licenceFront' : 'licenceBack']}
                fetchBlob={() => api.get(`/employees/${licenceFor._id}/licence/${side}`, { responseType:'blob' }).then(r => r.data)}/>
            ))}
            <div style={{ fontSize:'11px', color:'var(--hint)' }}>Each time a licence is viewed it is recorded in the activity log.</div>
          </div>
        </div>
      )}
    </div>
  );
}
